import React from 'react';
import { Check, Star, Info } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface PricingTier {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  isPopular?: boolean;
  isCurrent?: boolean;
}
interface PricingCardProps {
  tier: PricingTier;
  billingPeriod: 'monthly' | 'yearly';
  onSelectTier: (tierId: string, billingPeriod: 'monthly' | 'yearly', actionType?: 'upgrade' | 'downgrade' | 'cancel' | 'switch') => void;
  currentUserTier?: string | null;
  currentBillingPeriod?: 'monthly' | 'yearly' | null;
  isProcessing?: boolean;
  className?: string;
}
const PricingCard: React.FC<PricingCardProps> = ({
  tier,
  billingPeriod,
  onSelectTier,
  currentUserTier,
  currentBillingPeriod,
  isProcessing = false,
  className = ""
}) => {
  const price = billingPeriod === 'monthly' ? tier.monthlyPrice : tier.yearlyPrice;
  const monthlyEquivalent = billingPeriod === 'yearly' ? tier.yearlyPrice / 12 : tier.monthlyPrice;
  const savings = billingPeriod === 'yearly' ? tier.monthlyPrice * 12 - tier.yearlyPrice : 0;
  const savingsPercentage = billingPeriod === 'yearly' ? Math.round(savings / (tier.monthlyPrice * 12) * 100) : 0;

  // Enhanced button logic with proration information
  const getButtonInfo = () => {
    const tierName = tier.name.toLowerCase();
    const currentTier = currentUserTier?.toLowerCase();
    console.log('PricingCard button logic:', {
      tierName,
      currentTier,
      billingPeriod,
      currentBillingPeriod,
      isExactMatch: tierName === currentTier && billingPeriod === currentBillingPeriod
    });

    // Check if this is exactly the current plan (same tier AND billing period)
    const isExactCurrentPlan = tierName === currentTier && billingPeriod === currentBillingPeriod;
    if (isExactCurrentPlan) {
      return {
        text: 'Current Plan',
        disabled: true,
        action: undefined,
        showProration: false
      };
    }
    if (tierName === 'free') {
      // If user is on a paid plan, show "Cancel Subscription" for free plan
      if (currentTier && currentTier !== 'free') {
        return {
          text: 'Cancel Subscription',
          disabled: false,
          action: 'cancel' as const,
          showProration: false
        };
      }
      return {
        text: 'Current Plan',
        disabled: true,
        action: undefined,
        showProration: false
      };
    }

    // For paid plans with existing subscription
    if (currentTier && currentTier !== 'free') {
      // Same tier, different billing period - this is a billing period switch
      if (tierName === currentTier && billingPeriod !== currentBillingPeriod) {
        const periodText = billingPeriod === 'yearly' ? 'Yearly' : 'Monthly';
        const savingsText = billingPeriod === 'yearly' ? ` (Save ${savingsPercentage}%)` : '';
        return {
          text: `Switch to ${periodText}${savingsText}`,
          disabled: false,
          action: 'switch' as const,
          showProration: true
        };
      }

      // Different tier - check if upgrade or downgrade
      const tierOrder = {
        'free': 0,
        'plus': 1,
        'pro': 2
      };
      const currentTierLevel = tierOrder[currentTier as keyof typeof tierOrder] || 0;
      const targetTierLevel = tierOrder[tierName as keyof typeof tierOrder] || 0;
      if (targetTierLevel < currentTierLevel) {
        return {
          text: 'Downgrade',
          disabled: false,
          action: 'downgrade' as const,
          showProration: true
        };
      } else if (targetTierLevel > currentTierLevel) {
        const periodText = billingPeriod === 'yearly' ? ' Yearly' : ' Monthly';
        return {
          text: `Upgrade to ${tier.name}${periodText}`,
          disabled: false,
          action: 'upgrade' as const,
          showProration: true
        };
      }
    }

    // New subscription
    const periodText = billingPeriod === 'yearly' ? ' Yearly' : ' Monthly';
    return {
      text: `Get ${tier.name}${periodText}`,
      disabled: false,
      action: 'upgrade' as const,
      showProration: false
    };
  };
  const buttonInfo = getButtonInfo();
  const isDisabled = buttonInfo.disabled || isProcessing;
  const isCurrentPlan = tier.name.toLowerCase() === currentUserTier?.toLowerCase() && billingPeriod === currentBillingPeriod;
  const handleClick = () => {
    if (!isDisabled) {
      console.log('PricingCard click:', {
        tierName: tier.name.toLowerCase(),
        billingPeriod,
        action: buttonInfo.action,
        currentTier: currentUserTier,
        currentBillingPeriod,
        price,
        yearlyPrice: tier.yearlyPrice,
        monthlyPrice: tier.monthlyPrice
      });
      onSelectTier(tier.name.toLowerCase(), billingPeriod, buttonInfo.action);
    }
  };
  return <Card className={`relative overflow-hidden h-full min-h-[520px] flex flex-col transition-all duration-200 hover:shadow-lg ${tier.isPopular ? 'border-primary ring-2 ring-primary/20 scale-105' : isCurrentPlan ? 'border-green-500 ring-2 ring-green-500/20' : 'border-border hover:border-primary/30'} ${className}`}>
      {tier.isPopular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          
        </div>}
      
      {isCurrentPlan && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <Badge className="bg-green-500 text-white px-3 py-1 text-sm font-bold shadow-lg">
            <Check className="h-3 w-3 mr-1" />
            Current Plan
          </Badge>
        </div>}

      <CardHeader className="text-center pb-6 border-b pt-16">
        <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
        <p className="text-muted-foreground mb-4 min-h-[2.5rem] flex items-center justify-center text-sm">
          {tier.description}
        </p>
        
        <div className="space-y-2">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-4xl font-bold">${price.toFixed(price < 1 ? 2 : 0)}</span>
            <span className="text-muted-foreground text-sm">
              /{billingPeriod === 'yearly' ? 'year' : 'month'}
            </span>
          </div>
          
          {billingPeriod === 'yearly' && tier.yearlyPrice > 0 && <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                ${monthlyEquivalent.toFixed(2)}/month billed annually
              </p>
              {savings > 0 && <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                  Save ${savings} ({savingsPercentage}% off)
                </Badge>}
            </div>}
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-6">
        <div className="mb-6">
          <Button className="w-full font-semibold" variant={isCurrentPlan ? 'outline' : tier.isPopular ? 'default' : 'outline'} onClick={handleClick} disabled={isDisabled} size="lg">
            {isProcessing ? 'Processing...' : buttonInfo.text}
          </Button>
          
          {buttonInfo.showProration && !isCurrentPlan && <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <p className="font-medium">Prorated billing</p>
                  <p>You'll be credited for your remaining subscription time and only pay the difference.</p>
                </div>
              </div>
            </div>}
        </div>
        
        <div className="space-y-3 flex-1">
          {tier.features.map((feature, index) => <div key={index} className="flex items-start gap-3">
              <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-sm leading-relaxed break-words">{feature}</span>
            </div>)}
        </div>
      </CardContent>
    </Card>;
};
export default PricingCard;